/**
 * Petits pictos SVG maison, `currentColor`, en remplacement des emoji dans
 * l'UI (🔥, 🥇🥈🥉…) : le rendu d'un emoji dépend de la police système du
 * visiteur (Apple/Google/Microsoft dessinent une flamme ou une médaille très
 * différemment, parfois en couleur imposée qui jure avec le thème), alors
 * qu'un trait `currentColor` reste net et cohérent partout, à la manière de
 * la croix de fermeture des popups. Ne remplace jamais l'emoji d'un texte
 * *partagé* (ex. le grille Wordle-style copiée dans le presse-papiers,
 * `DailyChallenge.js`) : là, c'est du texte brut destiné à d'autres apps,
 * pas du HTML.
 */

/**
 * Série en cours (Défi quotidien) : silhouette pleine, pas un simple contour
 * — une flamme se lit d'un coup d'œil à sa forme ET sa couleur, un trait fin
 * la fait passer pour une icône générique. Couleur fixe (`.picto-flame` dans
 * `_leaderboard.scss`), pas `currentColor` hérité du texte ambiant : la
 * flamme doit rester la même orange vive partout, comme l'était l'emoji.
 */
export const PICTO_FLAME = `<svg class="picto picto-flame" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2c.7 3.2-1 4.8-2.6 6.8C7.9 11 6 13.4 6 16c0 3.9 2.7 7 6 7s6-3.1 6-7c0-2.1-1-4-2.1-5.5-.2 1.6-1.1 2.9-2.2 2.9-1.3 0-1.7-1.6-1.1-3.3.8-2.3.2-5.4-.6-7.1Z"/></svg>`;

/** Temps mesuré. */
export const PICTO_CLOCK = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l4 2"/></svg>`;

/** Erreurs (modes sans vies). */
export const PICTO_CROSS = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12M18 6 6 18"/></svg>`;

/** Score moyen. */
export const PICTO_CHART = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 20V10M12 20V4M19 20v-7"/></svg>`;

const MEDAL_RANK_CLASS = { 1: "gold", 2: "silver", 3: "bronze" };

/**
 * Médaille des 3 premiers rangs (podium, tableau, mini profil) : disque plein
 * + ruban, avec le rang gravé au centre — un simple cercle au trait se
 * lisait comme un pictogramme générique, pas un trophée, et le numéro évite
 * de le confondre avec une puce décorative. Coloriée par rang via une classe
 * modificatrice (le site a son propre or/argent/bronze, `#ffd700`/`#c0c0c0`/
 * `#cd7f32`, déjà utilisés ailleurs dans `_leaderboard.scss`). `null`
 * au-delà du podium : à afficher en `#N` classique.
 */
export function medalPicto(rank) {
    const tone = MEDAL_RANK_CLASS[rank];
    if (!tone) return null;
    return `<svg class="picto picto-medal picto-medal--${tone}" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8.5 13 5 21.5l7-3 7 3-3.5-8.5" opacity="0.85"/>
        <circle cx="12" cy="9" r="7"/>
        <text x="12" y="13" text-anchor="middle" font-size="10" font-weight="800" font-family="inherit" fill="black" fill-opacity="0.85">${rank}</text>
    </svg>`;
}

// --- Pictos des nouveautés (passeport, révision, accueil, rappels, Lab) ------
// Même règle que ci-dessus : jamais d'emoji dans l'interface. Les trois tampons
// du passeport ont chacun leur forme, pour se distinguer sans couleur.

/** Tampon « drapeau » du passeport. */
export const PICTO_FLAG = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21V4"/><path d="M5 4h11l-2 4 2 4H5"/></svg>`;

/** Tampon « capitale » : un édifice à colonnes. */
export const PICTO_CAPITAL = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10h18L12 4 3 10Z"/><path d="M5 10v8M9.5 10v8M14.5 10v8M19 10v8M3 20h18"/></svg>`;

/** Tampon « carte » : une carte pliée. */
export const PICTO_MAP = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 4 3 6v14l6-2 6 2 6-2V4l-6 2-6-2Z"/><path d="M9 4v14M15 6v14"/></svg>`;

/** Passeport : un carnet frappé d'un globe. */
export const PICTO_PASSPORT = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="3" width="14" height="18" rx="2"/><circle cx="12" cy="11" r="3.5"/><path d="M8.5 11h7M12 7.5c1.2 1 1.2 6 0 7M9 17h6"/></svg>`;

/** Révision : deux flèches qui reviennent sur elles-mêmes. */
export const PICTO_REVIEW = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 11a8 8 0 0 0-14.3-4.9L4 8"/><path d="M4 3v5h5"/><path d="M4 13a8 8 0 0 0 14.3 4.9L20 16"/><path d="M20 21v-5h-5"/></svg>`;

/** Record personnel : une coupe. */
export const PICTO_TROPHY = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 4h8v5a4 4 0 0 1-8 0V4Z"/><path d="M8 6H5v1a3 3 0 0 0 3 3M16 6h3v1a3 3 0 0 1-3 3M12 13v4M8 20h8M9.5 17h5"/></svg>`;

/** Fait (défi du jour déjà joué). */
export const PICTO_CHECK = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12.5 4.5 4.5L19 7.5"/></svg>`;

/** Reprendre une partie. */
export const PICTO_PLAY = `<svg class="picto" viewBox="7.5 4 13.5 16" fill="currentColor"><path d="M8 5.5v13a1 1 0 0 0 1.5.9l10.2-6.5a1 1 0 0 0 0-1.8L9.5 4.6A1 1 0 0 0 8 5.5Z"/></svg>`;

/** Joker de série : un bouclier qui protège la série d'un jour manqué. */
export const PICTO_SHIELD = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3 5 6v5c0 4.5 3 8.3 7 10 4-1.7 7-5.5 7-10V6l-7-3Z"/><path d="m9 12 2 2 4-4"/></svg>`;

/** Rappel quotidien : une cloche. */
export const PICTO_BELL = `<svg class="picto" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15L6 16Z"/><path d="M10 20.5a2 2 0 0 0 4 0"/></svg>`;

/** Note d'un mode du Lab : une étoile pleine (sa couleur dit si elle est allumée). */
export const PICTO_STAR = `<svg class="picto" viewBox="0 0 24 24" fill="currentColor"><path d="m12 3 2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9L12 3Z"/></svg>`;
